import { todayStr } from "../domain/date";
import type {
  CustomFood,
  CycleState,
  DayLog,
  WalkLog,
  EatingProfile,
  JointAnswer,
  KayaState,
  MealPlan,
  MealPlanDay,
  MealTemplate,
  Measure,
  SessionKey,
  TrainingProfile,
  WaterSettings,
} from "../domain/types";

/** Two devices can both be offline and both log work. Rather than letting one overwrite the
 *  other, state merges field by field: additive collections union, day logs merge per day,
 *  and only genuinely scalar settings fall back to last-write-wins. */

type Stamps = Record<string, number>;

/**
 * The stamp a key carries when it was written by a build that did not stamp anything.
 *
 * It has to be lower than every real timestamp and higher than the absence of a tombstone, so
 * that a legacy entry survives on its own but loses to any removal — which is sound, because a
 * tombstone can only have been written by a later build than the one that omitted the stamps.
 */
const LEGACY_STAMP = 1;

/** How long a tombstone is kept. Past this a device that has been offline the whole time can
 *  resurrect the key it deleted; three months is far longer than any real offline stretch, and
 *  the alternative is a map that grows for the life of the account. */
const TOMBSTONE_TTL_MS = 90 * 24 * 60 * 60 * 1000;

function maxStamps(a: Stamps | undefined, b: Stamps | undefined): Stamps {
  const out: Stamps = { ...(a || {}) };
  for (const [k, v] of Object.entries(b || {})) out[k] = Math.max(out[k] ?? 0, v);
  return out;
}

interface Tombstoned<V> {
  values: Record<string, V>;
  at: Stamps;
  off: Stamps;
}

/**
 * Unions a keyed collection that either device may also remove from.
 *
 * `{ ...a, ...b }` cannot express a removal. Whichever device still holds the key puts it back
 * on the next sync and then pushes it, which is exactly why travel mode switched itself on again
 * a few seconds after being switched off, and why a period logged by mistake could not be taken
 * back. So every add and every removal is stamped, and a key survives only when its most recent
 * add is newer than its most recent removal. Both stamp maps are carried forward rather than
 * cleared, because a device that re-adds a key must out-stamp a tombstone it has not yet seen.
 */
function mergeTombstoned<V>(
  a: Tombstoned<V>,
  b: Tombstoned<V>,
  bNewer: boolean,
  now: number
): Tombstoned<V> {
  const at = maxStamps(a.at, b.at);
  const off = maxStamps(a.off, b.off);

  const values: Record<string, V> = {};
  for (const k of new Set([...Object.keys(a.values), ...Object.keys(b.values)])) {
    if ((at[k] ?? LEGACY_STAMP) <= (off[k] ?? 0)) continue;

    const inA = k in a.values;
    const inB = k in b.values;
    if (inA && inB && a.values[k] !== b.values[k]) {
      // Both still hold the key but disagree on its value — a session swapped to different
      // things on two phones. The side that wrote more recently meant it more recently.
      const aAt = a.at[k] ?? LEGACY_STAMP;
      const bAt = b.at[k] ?? LEGACY_STAMP;
      values[k] =
        aAt === bAt ? (bNewer ? b.values[k] : a.values[k]) : aAt > bAt ? a.values[k] : b.values[k];
    } else {
      values[k] = inA ? a.values[k] : b.values[k];
    }
  }

  const liveAt: Stamps = {};
  for (const k of Object.keys(values)) if (at[k] !== undefined) liveAt[k] = at[k];

  const liveOff: Stamps = {};
  for (const [k, t] of Object.entries(off)) if (now - t < TOMBSTONE_TTL_MS) liveOff[k] = t;

  return { values: values, at: liveAt, off: liveOff };
}

/**
 * Ticks, bucket by bucket. A tick only ever gets added, so two copies union — except that "Clear
 * today's progress" takes them all back, and a plain union handed them straight back from any
 * phone that still held them. A clear is stamped in `doneOff`, and each side's ticks in a bucket
 * survive only when that side's last tick there (`doneAt`) is newer than the bucket's last clear.
 * A copy from before the stamps carries the legacy stamp: it survives on its own and loses to any
 * clear, the tombstone rule everywhere else. (ADR-050)
 */
function mergeDone(a: DayLog, b: DayLog): Pick<DayLog, "done" | "doneAt" | "doneOff"> {
  const off = maxStamps(a.doneOff, b.doneOff);
  const done: DayLog["done"] = {};
  const at: Stamps = {};
  for (const k of new Set([...Object.keys(a.done || {}), ...Object.keys(b.done || {})])) {
    let ticks: Record<number, 1> = {};
    for (const side of [a, b]) {
      const mine = side.done?.[k];
      if (!mine) continue;
      const stamp = side.doneAt?.[k];
      if ((stamp ?? LEGACY_STAMP) <= (off[k] ?? 0)) continue;
      ticks = { ...ticks, ...mine };
      if (stamp !== undefined) at[k] = Math.max(at[k] ?? 0, stamp);
    }
    done[k] = ticks;
  }
  return {
    done,
    doneAt: Object.keys(at).length ? at : undefined,
    doneOff: Object.keys(off).length ? off : undefined,
  };
}

/** The day's own session's buckets; a rescue and an extra keep theirs and are never cleared. */
function ownBucket(k: string): boolean {
  return k !== "rescue" && !k.startsWith("extra:");
}

/**
 * The copy still holds the day's own session from before its clear: ticks no newer than the
 * bucket's clear, or a finish stamped before it. Such a copy's finish, kcal and clock are what the
 * clear took back, and a max or an OR would put them back with the ticks.
 */
function heldBeforeClear(L: DayLog, off: Stamps): boolean {
  let cut = 0;
  for (const [k, t] of Object.entries(off)) {
    if (!ownBucket(k)) continue;
    cut = Math.max(cut, t);
    const mine = L.done?.[k];
    if (mine && Object.keys(mine).length && (L.doneAt?.[k] ?? LEGACY_STAMP) <= t) return true;
  }
  if (!cut || !L.completedAt) return false;
  const fin = Date.parse(L.completedAt);
  return Number.isFinite(fin) && fin <= cut;
}

function withoutCleared(L: DayLog): DayLog {
  const out = { ...L };
  delete out.completedAt;
  delete out.kcal;
  delete out.elapsedMs;
  return out;
}

/** What the day ran: the later freeze, unless the clear of its bucket came after it. (ADR-050) */
function laterRan(a: DayLog, b: DayLog, off: Stamps): DayLog["ran"] {
  const live = [a.ran, b.ran].filter((r): r is NonNullable<DayLog["ran"]> => !!r && r.at > (off[r.key] ?? 0));
  return live.reduce<DayLog["ran"]>((w, r) => (!w || r.at > w.at ? r : w), undefined);
}

/** A block merged whole by its own `at`: a mix of two answers is an answer nobody gave. */
function laterBlock<T extends { at: number }>(a: T | undefined, b: T | undefined): T | undefined {
  if (!a || !b) return a || b;
  return (b.at || 0) > (a.at || 0) ? b : a;
}

/**
 * A list of id-carrying items two phones can both add to and either can take back from.
 *
 * `{ ...older, ...newer }` would hand the whole array to whichever device wrote last, losing the
 * other one's lunch. Items carry ids, so they union; removals carry tombstones, so a deleted
 * item does not come back on the next sync — the same rule as travel days, for the same reason.
 * Items are never edited in place (an edit is a removal plus a new id), so a tombstone always
 * wins over the id it names. Used for the food log and the meal plan alike. (ADR-019, ADR-025,
 * ADR-026)
 */
function mergeById<E extends { id: string; at: number }>(
  aItems: E[] | undefined,
  aOff: Stamps | undefined,
  bItems: E[] | undefined,
  bOff: Stamps | undefined
): { items: E[] | undefined; off: Stamps | undefined } {
  const off = maxStamps(aOff, bOff);
  const byId = new Map<string, E>();
  for (const e of [...(aItems || []), ...(bItems || [])]) {
    if (e.id in off) continue;
    const seen = byId.get(e.id);
    // Same item written on both — the later write is the one that meant it.
    if (!seen || e.at > seen.at) byId.set(e.id, e);
  }
  const items = [...byId.values()].sort((x, y) => x.at - y.at);
  return {
    items: items.length ? items : undefined,
    off: Object.keys(off).length ? off : undefined,
  };
}

function mergeFood(a: DayLog, b: DayLog): Pick<DayLog, "food" | "foodOff"> {
  const m = mergeById(a.food, a.foodOff, b.food, b.foodOff);
  return { food: m.items, foodOff: m.off };
}

/** Drinks: the food log's rule, verbatim. Without it `mergeDay`'s spread handed the whole list
 *  to whichever phone wrote last, and two phones logging glasses on one day lost one's. (ADR-044) */
function mergeWater(a: DayLog, b: DayLog): Pick<DayLog, "water" | "waterOff"> {
  const m = mergeById(a.water, a.waterOff, b.water, b.waterOff);
  return { water: m.items, waterOff: m.off };
}

/**
 * The hot-day switch. It can be switched off again, so an OR would bring it back from any copy
 * taken while it was on; the later switch wins instead. Unstamped on both sides it cannot have
 * been cleared, so either copy being on means on. (ADR-044)
 */
function hotDay(a: DayLog, b: DayLog): Pick<DayLog, "hot" | "hotAt"> {
  const aAt = a.hotAt ?? LEGACY_STAMP;
  const bAt = b.hotAt ?? LEGACY_STAMP;
  const w = aAt === bAt ? null : aAt > bAt ? a : b;
  const on = w ? !!w.hot : !!(a.hot || b.hot);
  return { hot: on ? true : undefined, hotAt: w ? w.hotAt : a.hotAt };
}

/** Planned days are kept this long. A plan is not history — what was eaten is in the log — and
 *  without a horizon every day ever planned would ride along in every sync. */
const PLAN_KEEP_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * The meal plan: each day's items by id with tombstones, templates as a tombstoned keyed
 * collection whose value conflicts go to the later `at`. Undefined when neither side has one, so
 * a blob from before the plan existed merges unchanged. (ADR-026)
 */
function mergeMealPlan(
  a: MealPlan | undefined,
  b: MealPlan | undefined,
  bNewer: boolean,
  now: number
): MealPlan | undefined {
  if (!a && !b) return undefined;
  const A = a || {};
  const B = b || {};
  const cutoff = todayStr(new Date(now - PLAN_KEEP_MS));

  const days: Record<string, MealPlanDay> = {};
  for (const ds of new Set([...Object.keys(A.days || {}), ...Object.keys(B.days || {})])) {
    if (ds < cutoff) continue;
    const x = A.days?.[ds];
    const y = B.days?.[ds];
    const m = mergeById(x?.items, x?.off, y?.items, y?.off);
    if (m.items || m.off) days[ds] = { ...(m.items ? { items: m.items } : {}), ...(m.off ? { off: m.off } : {}) };
  }

  const tpl = mergeTombstoned<MealTemplate>(
    { values: A.templates || {}, at: A.templatesAt || {}, off: A.templatesOff || {} },
    { values: B.templates || {}, at: B.templatesAt || {}, off: B.templatesOff || {} },
    bNewer,
    now
  );

  return { days, templates: tpl.values, templatesAt: tpl.at, templatesOff: tpl.off };
}

/** A day's symptoms as a keyed collection with tombstones, the travel-day rule: an un-pick, or the
 *  others that "Feeling fine" cleared, must not come back from an older copy. (ADR-019) */
function mergeSymptoms(
  a: DayLog,
  b: DayLog,
  bNewer: boolean,
  now: number
): Pick<DayLog, "symptoms" | "symptomsAt" | "symptomsOff"> {
  const asMap = (xs: string[] | undefined): Record<string, 1> => {
    const out: Record<string, 1> = {};
    for (const k of xs || []) out[k] = 1;
    return out;
  };
  const m = mergeTombstoned<1>(
    { values: asMap(a.symptoms), at: a.symptomsAt || {}, off: a.symptomsOff || {} },
    { values: asMap(b.symptoms), at: b.symptomsAt || {}, off: b.symptomsOff || {} },
    bNewer,
    now
  );
  return {
    // in the order they were first picked, as the chips list them
    symptoms: Array.from(new Set([...(a.symptoms || []), ...(b.symptoms || [])])).filter(
      (k) => k in m.values
    ),
    symptomsAt: Object.keys(m.at).length ? m.at : undefined,
    symptomsOff: Object.keys(m.off).length ? m.off : undefined,
  };
}

/**
 * The rescue/extra running clock.
 *
 * Between two finishes it only grows, so two copies of the same stretch merge by the larger value
 * like every other clock here. A finish zeroes it, and under a plain max the zero could never win:
 * the first sync after a finish handed the mid-session copy back, the next extra of the day
 * started its clock from it, and that time was banked and priced twice. So the finish stamps
 * `extraElapsedAt`, and the later stamp wins outright; only equal stamps — the same stretch, or
 * two copies from before the stamp existed — fall back to the max. (ADR-034)
 */
/**
 * The day's walks, keyed by when each began: a plain `{...older, ...newer}` took one copy's whole
 * array, so the second walk of a day vanished on the next sync whenever the other copy was judged
 * newer — the first walk stayed, every later one went. A walk is never removed by hand, so a union
 * needs no tombstone; for the same start, the copy with measured steps is the fuller one (the
 * backfill is exact and arrives later), else the later finish, and a declared distance survives
 * from either side.
 */
function richerWalk(x: WalkLog, y: WalkLog): WalkLog {
  const xm = typeof x.measuredSteps === "number";
  const ym = typeof y.measuredSteps === "number";
  const keep = xm !== ym ? (xm ? x : y) : (x.at || "") >= (y.at || "") ? x : y;
  const other = keep === x ? y : x;
  return typeof keep.kmDeclared !== "number" && typeof other.kmDeclared === "number"
    ? { ...keep, kmDeclared: other.kmDeclared }
    : keep;
}

function mergeWalks(a: DayLog, b: DayLog): Pick<DayLog, "walks"> {
  if (!a.walks?.length && !b.walks?.length) return {};
  const by = new Map<string, WalkLog>();
  for (const w of [...(a.walks || []), ...(b.walks || [])]) {
    const cur = by.get(w.startedAt);
    by.set(w.startedAt, cur ? richerWalk(cur, w) : w);
  }
  return { walks: [...by.values()].sort((x, y) => x.startedAt.localeCompare(y.startedAt)) };
}

function extraClock(a: DayLog, b: DayLog): Pick<DayLog, "extraElapsed" | "extraElapsedAt"> {
  const aAt = a.extraElapsedAt ?? LEGACY_STAMP;
  const bAt = b.extraElapsedAt ?? LEGACY_STAMP;
  if (aAt !== bAt) {
    const w = aAt > bAt ? a : b;
    return { extraElapsed: w.extraElapsed || undefined, extraElapsedAt: w.extraElapsedAt };
  }
  return {
    extraElapsed: Math.max(a.extraElapsed || 0, b.extraElapsed || 0) || undefined,
    extraElapsedAt: a.extraElapsedAt,
  };
}

/** Two snapshots of a day's targets: the later one was taken against the later state. Taken
 *  whole, never field by field — a mix of two snapshots is targets no day ever had. (ADR-040) */
function laterTargets(a: DayLog["targets"], b: DayLog["targets"]): DayLog["targets"] {
  if (!a || !b) return a || b;
  return (b.at || 0) > (a.at || 0) ? b : a;
}

function mergeDay(x: DayLog | undefined, y: DayLog | undefined, bNewer: boolean, now: number): DayLog {
  if (!x) return y!;
  if (!y) return x;
  const off = maxStamps(x.doneOff, y.doneOff);
  const a = heldBeforeClear(x, off) ? withoutCleared(x) : x;
  const b = heldBeforeClear(y, off) ? withoutCleared(y) : y;
  const newer = bNewer ? b : a;
  const older = bNewer ? a : b;

  return {
    ...older,
    ...newer,
    ...mergeDone(a, b),
    ran: laterRan(a, b, off),
    howWas: laterBlock(a.howWas, b.howWas),
    tests: laterBlock(a.tests, b.tests),
    morning: laterBlock(a.morning, b.morning),
    // a finished session outranks an unfinished one regardless of clock skew
    completedAt: a.completedAt || b.completedAt,
    elapsedMs: Math.max(a.elapsedMs || 0, b.elapsedMs || 0) || undefined,
    ...extraClock(a, b),
    // banked totals only ever grow on the phone that finished them
    extraDoneMs: Math.max(a.extraDoneMs || 0, b.extraDoneMs || 0) || undefined,
    extras: Array.from(new Set([...(a.extras || []), ...(b.extras || [])])) as DayLog["extras"],
    ...mergeSymptoms(a, b, bNewer, now),
    ...mergeWalks(a, b),
    treadmill:
      a.treadmill && b.treadmill
        ? a.treadmill.at >= b.treadmill.at
          ? a.treadmill
          : b.treadmill
        : a.treadmill || b.treadmill,
    frozen: a.frozen || b.frozen,
    rescued: a.rescued || b.rescued,
    periodSkip: a.periodSkip || b.periodSkip,
    ...mergeFood(a, b),
    ...mergeWater(a, b),
    ...hotDay(a, b),
    targets: laterTargets(a.targets, b.targets),
  };
}

function mergeMeasures(a: Measure[], b: Measure[], bNewer: boolean): Measure[] {
  const byDate = new Map<string, Measure>();
  const first = bNewer ? a : b;
  const second = bNewer ? b : a;
  for (const m of first) byDate.set(m.date, { ...byDate.get(m.date), ...m });
  for (const m of second) byDate.set(m.date, { ...byDate.get(m.date), ...m });
  return Array.from(byDate.values()).sort((x, y) => (x.date < y.date ? -1 : 1));
}

export interface Stamped {
  state: KayaState;
  updatedAt: number;
}

/** Fills in the collections merging relies on, without pulling in profile defaults. */
function withDefaults(s: KayaState): KayaState {
  return {
    ...s,
    logs: s.logs || {},
    measures: s.measures || [],
    travelDates: s.travelDates || {},
    overrides: s.overrides || {},
    badges: s.badges || {},
    freezeEarned: s.freezeEarned || {},
    challenges: s.challenges || {},
    recapSeen: s.recapSeen || {},
    cycle: s.cycle || { starts: [], length: 28, periodLen: 5 },
  };
}

/**
 * Length, period length and contraception as one block, from whichever device stamped it last.
 * Following the newer blob instead would let a phone that touched anything else later hand back
 * its stale settings — and drop a contraception answer it never had. Unstamped on both sides (an
 * older build), the newer blob wins as before.
 */
function cycleSettings(
  a: CycleState,
  b: CycleState,
  bNewer: boolean
): Pick<CycleState, "length" | "periodLen" | "contraception" | "settingsAt"> {
  const aAt = a.settingsAt ?? 0;
  const bAt = b.settingsAt ?? 0;
  const src = aAt === bAt ? (bNewer ? b : a) : aAt > bAt ? a : b;
  const other = src === a ? b : a;
  // Assigned even when absent, so the losing side's answer is not left behind by the spread. The
  // one exception: contraception is never taken back by deleting it (the settings row writes
  // "none"), so a winner that simply never had an answer — an older build, or a phone that only
  // changed the cycle length — must not erase the answer the other side holds.
  return {
    length: src.length,
    periodLen: src.periodLen,
    contraception: src.contraception ?? other.contraception,
    settingsAt: src.settingsAt,
  };
}

/**
 * The water settings as one block from whichever device stamped it last — the cycle settings'
 * rule. The profile otherwise follows the phone that synced last, and a phone that only changed
 * the theme would hand back a bottle list, a sip size or a doctor's limit it never saw change.
 * Equal stamps (both untouched, or an older build on one side) follow the newer blob. (ADR-044)
 */
function waterBlock(
  a: WaterSettings | undefined,
  b: WaterSettings | undefined,
  bNewer: boolean
): WaterSettings | undefined {
  if (!a || !b) return a || b;
  const aAt = a.at ?? 0;
  const bAt = b.at ?? 0;
  return aAt === bAt ? (bNewer ? b : a) : aAt > bAt ? a : b;
}

/**
 * The eating questionnaire's answers as one block from whichever device saved it last — the
 * water block's rule, because the profile otherwise follows whichever phone synced last and a
 * phone that only changed the theme would hand back answers it never saw change. (ADR-042)
 *
 * Two lists are the exception: `avoid` and `dislikes` merge key by key with tombstones, the
 * travel days' rule. A dislike added on one phone and an allergy taken back on the other both
 * survive, whichever block wins; a plain whole-block rule would drop one of them, and a plain
 * union would put back the one that was taken back. An answered list stays an answer: when
 * either side answered it, the merged list is present even if it came out empty.
 */
function eatingBlock(
  a: EatingProfile | undefined,
  b: EatingProfile | undefined,
  bNewer: boolean,
  now: number
): EatingProfile | undefined {
  if (!a || !b) return a || b;
  const aAt = a.at ?? 0;
  const bAt = b.at ?? 0;
  const src = aAt === bAt ? (bNewer ? b : a) : aAt > bAt ? a : b;
  const other = src === a ? b : a;
  const out: EatingProfile = { ...src };
  for (const key of ["avoid", "dislikes"] as const) {
    const atKey = `${key}At` as const;
    const offKey = `${key}Off` as const;
    const sl: readonly string[] | undefined = src[key];
    const ol: readonly string[] | undefined = other[key];
    delete out[key];
    delete out[atKey];
    delete out[offKey];
    if (sl === undefined && ol === undefined) continue;
    const m = mergeTombstoned<1>(
      { values: listAsMap(sl), at: src[atKey] || {}, off: src[offKey] || {} },
      { values: listAsMap(ol), at: other[atKey] || {}, off: other[offKey] || {} },
      src === b,
      now
    );
    // the winning block's order first, so the chips the person arranged stay where they were
    const keys = [...new Set([...(sl || []), ...(ol || [])])].filter((k) => k in m.values);
    (out as Record<string, unknown>)[key] = keys;
    if (Object.keys(m.at).length) out[atKey] = m.at;
    if (Object.keys(m.off).length) out[offKey] = m.off;
  }
  return out;
}

/**
 * The exercise answers as one block from whichever device saved it last — the eating block's rule,
 * for the same reason, and because a linked owner writes it from another phone (ADR-046). `have`
 * and `joints` merge key by key with tombstones, as `eating.avoid` does: equipment added on one
 * phone and a knee taken back on the other both survive whichever block wins. A joint held on
 * both sides with different answers goes to the side that stamped it later. (ADR-050)
 */
function trainingBlock(
  a: TrainingProfile | undefined,
  b: TrainingProfile | undefined,
  bNewer: boolean,
  now: number
): TrainingProfile | undefined {
  if (!a || !b) return a || b;
  const aAt = a.at ?? 0;
  const bAt = b.at ?? 0;
  const src = aAt === bAt ? (bNewer ? b : a) : aAt > bAt ? a : b;
  const other = src === a ? b : a;
  const out: TrainingProfile = { ...src };

  delete out.have;
  delete out.haveAt;
  delete out.haveOff;
  if (src.have !== undefined || other.have !== undefined) {
    const m = mergeTombstoned<1>(
      { values: listAsMap(src.have), at: src.haveAt || {}, off: src.haveOff || {} },
      { values: listAsMap(other.have), at: other.haveAt || {}, off: other.haveOff || {} },
      false,
      now
    );
    out.have = [...new Set([...(src.have || []), ...(other.have || [])])].filter((k) => k in m.values);
    if (Object.keys(m.at).length) out.haveAt = m.at;
    if (Object.keys(m.off).length) out.haveOff = m.off;
  }

  delete out.joints;
  delete out.jointsAt;
  delete out.jointsOff;
  if (src.joints !== undefined || other.joints !== undefined) {
    const m = mergeTombstoned<JointAnswer>(
      { values: (src.joints || {}) as Record<string, JointAnswer>, at: src.jointsAt || {}, off: src.jointsOff || {} },
      { values: (other.joints || {}) as Record<string, JointAnswer>, at: other.jointsAt || {}, off: other.jointsOff || {} },
      false, // a joint both sides stamped alike keeps the winning block's answer
      now
    );
    out.joints = m.values;
    if (Object.keys(m.at).length) out.jointsAt = m.at;
    if (Object.keys(m.off).length) out.jointsOff = m.off;
  }
  return out;
}

function withTraining(p: KayaState["profile"], t: TrainingProfile | undefined): KayaState["profile"] {
  return t ? { ...p, training: t } : p;
}

function listAsMap(list: readonly string[] | undefined): Record<string, 1> {
  const out: Record<string, 1> = {};
  for (const k of list || []) out[k] = 1;
  return out;
}

function withEating(p: KayaState["profile"], e: EatingProfile | undefined): KayaState["profile"] {
  return e ? { ...p, eating: e } : p;
}

const DINNER_KEYS = ["dinnerRemindOn", "dinnerRemindAt", "dinnerRemindSetAt"] as const;

/**
 * The "Log dinner?" switch and time as one pair from whichever side stamped it later. A linked
 * account sets it from another phone, and the profile otherwise follows the phone that synced
 * last — the parent's own phone, which never saw the change, would hand back its copy. Equal
 * stamps (both unstamped, or the same change) leave the profile's own rule alone. (ADR-046)
 */
function withDinner(
  p: KayaState["profile"],
  a: KayaState["profile"] | undefined,
  b: KayaState["profile"] | undefined
): KayaState["profile"] {
  const aAt = a?.dinnerRemindSetAt ?? 0;
  const bAt = b?.dinnerRemindSetAt ?? 0;
  if (aAt === bAt) return p;
  const src = aAt > bAt ? a : b;
  const out = { ...p };
  for (const k of DINNER_KEYS) {
    const v = src?.[k];
    if (v === undefined) delete out[k];
    else (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

/** The period starts as a keyed collection, so the same tombstone rules apply to them. */
function startsAsMap(s: KayaState): Record<string, 1> {
  const out: Record<string, 1> = {};
  for (const ds of s.cycle.starts || []) out[ds] = 1;
  return out;
}

function withWater(p: KayaState["profile"], w: WaterSettings | undefined): KayaState["profile"] {
  return w ? { ...p, water: w } : p;
}

function withPlan(p: MealPlan | undefined): Pick<KayaState, "mealPlan"> {
  return p ? { mealPlan: p } : {};
}

/** The user's own foods: the food log's rule, verbatim — union by id, a tombstone wins, an edit is
 *  a tombstone plus a new id. (ADR-026) */
function mergeCustomFoods(a: KayaState, b: KayaState): Pick<KayaState, "customFoods" | "customFoodsOff"> {
  const m = mergeById<CustomFood>(a.customFoods, a.customFoodsOff, b.customFoods, b.customFoodsOff);
  return { customFoods: m.items || [], customFoodsOff: m.off || {} };
}

/**
 * The day marks — untracked and fast day — as two tombstoned date sets, the travel days' rule: a
 * mark switched off must not come back from a copy taken while it was on. Absent from the result
 * when neither side has ever held one, so a blob from before the marks merges unchanged. (ADR-049)
 */
function mergeMarks(
  a: KayaState,
  b: KayaState,
  bNewer: boolean,
  now: number
): Pick<KayaState, "untrackedDates" | "untrackedAt" | "untrackedOff" | "fastDates" | "fastAt" | "fastOff"> {
  const out: Pick<KayaState, "untrackedDates" | "untrackedAt" | "untrackedOff" | "fastDates" | "fastAt" | "fastOff"> = {};
  const untracked = (s: KayaState) => !!(s.untrackedDates || s.untrackedAt || s.untrackedOff);
  const fast = (s: KayaState) => !!(s.fastDates || s.fastAt || s.fastOff);
  if (untracked(a) || untracked(b)) {
    const m = mergeTombstoned<1>(
      { values: a.untrackedDates || {}, at: a.untrackedAt || {}, off: a.untrackedOff || {} },
      { values: b.untrackedDates || {}, at: b.untrackedAt || {}, off: b.untrackedOff || {} },
      bNewer,
      now
    );
    out.untrackedDates = m.values;
    out.untrackedAt = m.at;
    out.untrackedOff = m.off;
  }
  if (fast(a) || fast(b)) {
    const m = mergeTombstoned<1>(
      { values: a.fastDates || {}, at: a.fastAt || {}, off: a.fastOff || {} },
      { values: b.fastDates || {}, at: b.fastAt || {}, off: b.fastOff || {} },
      bNewer,
      now
    );
    out.fastDates = m.values;
    out.fastAt = m.at;
    out.fastOff = m.off;
  }
  return out;
}

export function mergeState(local: Stamped, remote: Stamped, now = Date.now()): KayaState {
  // Either side can be a sparse blob written by an older build, so nothing below may
  // assume a key is present.
  const a = withDefaults(local.state);
  const b = withDefaults(remote.state);
  const bNewer = remote.updatedAt > local.updatedAt;
  const newer = bNewer ? b : a;

  const dates = new Set([...Object.keys(a.logs), ...Object.keys(b.logs)]);
  const logs: KayaState["logs"] = {};
  for (const ds of dates) logs[ds] = mergeDay(a.logs[ds], b.logs[ds], bNewer, now);

  const travel = mergeTombstoned<1 | true>(
    { values: a.travelDates, at: a.travelAt || {}, off: a.travelOff || {} },
    { values: b.travelDates, at: b.travelAt || {}, off: b.travelOff || {} },
    bNewer,
    now
  );

  const overrides = mergeTombstoned<SessionKey>(
    { values: a.overrides, at: a.overridesAt || {}, off: a.overridesOff || {} },
    { values: b.overrides, at: b.overridesAt || {}, off: b.overridesOff || {} },
    bNewer,
    now
  );

  const starts = mergeTombstoned<1>(
    {
      values: startsAsMap(a),
      at: a.cycle.startsAt || {},
      off: a.cycle.startsOff || {},
    },
    {
      values: startsAsMap(b),
      at: b.cycle.startsAt || {},
      off: b.cycle.startsOff || {},
    },
    bNewer,
    now
  );

  // A period that has been taken back should not leave its end date behind to be read as a
  // second period's worth of history.
  const ends: Record<string, string> = {};
  for (const [k, v] of Object.entries({
    ...(a.cycle.ends || {}),
    ...(b.cycle.ends || {}),
  })) {
    if (k in starts.values) ends[k] = v;
  }

  return {
    // scalar settings follow the more recently touched device; the water, eating and training
    // blocks and the dinner reminder their own stamps
    profile: withDinner(
      withTraining(
        withEating(
          withWater(
            { ...(bNewer ? a.profile : b.profile), ...newer.profile },
            waterBlock(a.profile?.water, b.profile?.water, bNewer)
          ),
          eatingBlock(a.profile?.eating, b.profile?.eating, bNewer, now)
        ),
        trainingBlock(a.profile?.training, b.profile?.training, bNewer, now)
      ),
      a.profile,
      b.profile
    ),
    cycle: {
      ...newer.cycle,
      ...cycleSettings(a.cycle, b.cycle, bNewer),
      starts: Object.keys(starts.values).sort(),
      startsAt: starts.at,
      startsOff: starts.off,
      ends,
    },
    logs,
    measures: mergeMeasures(a.measures || [], b.measures || [], bNewer),
    travelDates: travel.values,
    travelAt: travel.at,
    travelOff: travel.off,
    overrides: overrides.values,
    overridesAt: overrides.at,
    overridesOff: overrides.off,
    installed: a.installed || b.installed,
    badges: { ...a.badges, ...b.badges },
    // freezes are spendable, so trust the newer device rather than summing and double-granting
    freezes: newer.freezes,
    freezeEarned: { ...a.freezeEarned, ...b.freezeEarned },
    challenges: { ...a.challenges, ...b.challenges },
    rescueFor: newer.rescueFor,
    recapSeen: { ...a.recapSeen, ...b.recapSeen },
    tmNormalToday: newer.tmNormalToday,
    // Listed explicitly: this literal is the whole merged state, and a key missing here is
    // deleted by the first sync. (ADR-026)
    ...withPlan(mergeMealPlan(a.mealPlan, b.mealPlan, bNewer, now)),
    ...mergeCustomFoods(a, b),
    ...mergeMarks(a, b, bNewer, now),
  };
}
